import React, { useRef } from 'react';
import { Button } from 'antd';
import {
  ThunderboltFilled,
  LoadingOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import './aiCrew.css';

/**
 * ExecuteBar - Pinned bottom dock for triggering crew creation and scheduling.
 *
 * Requirements (U9):
 * - Pinned to the bottom of the proposal area
 * - Text: "Create crew & schedule {selectedCount}"
 * - Disabled at zero selected
 * - Spinner during execution
 * - Double-click protected (guard in UI + guard in hook)
 */
export default function ExecuteBar({
  selectedCount = 0,
  headcount = 0,
  isExecuting = false,
  onExecute,
}) {
  const isDebouncingRef = useRef(false);

  const handleExecuteClick = (e) => {
    e.preventDefault();
    if (isDebouncingRef.current || isExecuting || selectedCount === 0) {
      return;
    }
    isDebouncingRef.current = true;
    setTimeout(() => {
      isDebouncingRef.current = false;
    }, 1500);

    if (typeof onExecute === 'function') {
      onExecute();
    }
  };

  const isShortfall = selectedCount > 0 && selectedCount < headcount;
  const isOver = selectedCount > headcount;
  const isTargetMet = selectedCount === headcount && headcount > 0;

  return (
    <div className="ai-crew-execute-bar" data-testid="ai-crew-execute-bar">
      <div className="ai-crew-execute-left">
        <div className="ai-crew-execute-count-badge">
          <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            {selectedCount}
          </span>
          <span style={{ color: '#64748b' }}>of {headcount} Selected</span>
        </div>

        {isTargetMet && (
          <span className="ai-crew-execute-badge-tag full">
            <CheckCircleFilled style={{ marginRight: 4 }} />
            Full Crew Ready
          </span>
        )}

        {isShortfall && (
          <span className="ai-crew-execute-badge-tag shortfall">
            <ExclamationCircleFilled style={{ marginRight: 4 }} />
            {headcount - selectedCount} below target
          </span>
        )}

        {isOver && (
          <span className="ai-crew-execute-badge-tag over">
            +{selectedCount - headcount} over target
          </span>
        )}
      </div>

      <div className="ai-crew-execute-right">
        <Button
          type="primary"
          size="large"
          icon={isExecuting ? <LoadingOutlined /> : <ThunderboltFilled />}
          loading={isExecuting}
          disabled={selectedCount === 0 || isExecuting}
          onClick={handleExecuteClick}
          className="ai-crew-execute-btn"
          data-testid="execute-crew-btn"
          id="ai-crew-execute-btn"
        >
          {isExecuting
            ? 'Creating crew & scheduling...'
            : `Confirm & Schedule ${selectedCount} Workers`}
        </Button>
      </div>
    </div>
  );
}
